import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Scale, Target } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const WEIGHT_FIELDS = [
  { key: "price", label: "Offer Price vs Budget" },
  { key: "category", label: "Asset Category" },
  { key: "type", label: "Asset Type" },
  { key: "village", label: "Village / Town" },
  { key: "area", label: "Area" },
];

export default function MatchConfig() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["matching-config"],
    queryFn: async () => (await api.get("/admin/matching-config")).data,
  });

  const [weights, setWeights] = useState(null);
  const [thresholds, setThresholds] = useState(null);

  useEffect(() => {
    if (data) {
      setWeights({ ...data.weights });
      setThresholds({ ...data.thresholds });
    }
  }, [data]);

  const weightSum = weights
    ? WEIGHT_FIELDS.reduce((s, f) => s + (Number(weights[f.key]) || 0), 0)
    : 0;
  const sumValid = Math.abs(weightSum - 100) < 0.01;

  const saveWeights = useMutation({
    mutationFn: async () => {
      const payload = {};
      WEIGHT_FIELDS.forEach((f) => (payload[f.key] = Number(weights[f.key])));
      return api.put("/admin/matching-config/weights", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matching-config"] });
      toast.success("Weights saved — applied to live matching");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save weights"),
  });

  const saveThresholds = useMutation({
    mutationFn: async () =>
      api.put("/admin/matching-config/thresholds", {
        strong_match: Number(thresholds.strong_match),
        possible_match_floor: Number(thresholds.possible_match_floor),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matching-config"] });
      toast.success("Thresholds saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save thresholds"),
  });

  if (isLoading || !weights || !thresholds) {
    return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="p-8 max-w-3xl" data-testid="match-config-page">
      <div className="label-eyebrow">Admin · Matching</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Matching Configuration</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        Tune the Lead Intelligence weights and match thresholds. These feed the live auto-match
        engine and the on-demand match scoring used across Leads and the Pipeline.
      </p>

      {/* Weights */}
      <div className="mt-7 bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <Scale className="h-4 w-4 text-slate-700" />
          <div className="font-display text-lg font-semibold text-slate-900">Lead Intelligence Weights</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {WEIGHT_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-xs text-slate-600">{f.label}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={weights[f.key]}
                  onChange={(e) => setWeights({ ...weights, [f.key]: e.target.value })}
                  className="font-mono-num text-right"
                  data-testid={`weight-input-${f.key}`}
                />
                <span className="text-sm text-slate-400">%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
          <div
            className={`text-sm font-semibold ${sumValid ? "text-emerald-700" : "text-red-600"}`}
            data-testid="weight-sum"
          >
            Total: {weightSum}% {sumValid ? "✓" : "(must equal 100%)"}
          </div>
          <Button
            onClick={() => saveWeights.mutate()}
            disabled={!sumValid || saveWeights.isPending}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="save-weights-btn"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save weights
          </Button>
        </div>
      </div>

      {/* Thresholds */}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-4 w-4 text-slate-700" />
          <div className="font-display text-lg font-semibold text-slate-900">Match Thresholds</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Strong Match (auto-create) ≥</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                value={thresholds.strong_match}
                onChange={(e) => setThresholds({ ...thresholds, strong_match: e.target.value })}
                className="font-mono-num text-right"
                data-testid="threshold-strong-match"
              />
              <span className="text-sm text-slate-400">%</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Score at/above this auto-creates a Suggested match + reminder.</p>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Possible Match floor ≥</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min="0"
                max="100"
                step="1"
                value={thresholds.possible_match_floor}
                onChange={(e) => setThresholds({ ...thresholds, possible_match_floor: e.target.value })}
                className="font-mono-num text-right"
                data-testid="threshold-possible-floor"
              />
              <span className="text-sm text-slate-400">%</span>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Lower bound for showing a pair as a possible match.</p>
          </div>
        </div>
        <div className="flex justify-end mt-5 pt-4 border-t border-slate-100">
          <Button
            onClick={() => saveThresholds.mutate()}
            disabled={saveThresholds.isPending}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="save-thresholds-btn"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save thresholds
          </Button>
        </div>
      </div>
    </div>
  );
}
